import { useMutation, useQuery } from '@apollo/client';
import cx from 'classnames';
import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { apiClient } from '../../../../services/api/client';
import {
  ADD_SUBMISSION_REVIEW,
  PUBLISH_SUBMISSION_REVIEWS,
  SUBMISSION_REVIEWS,
  SubmissionReviewItem,
} from '../../../../services/graphql/submissionReview.operations';
import { SubmissionStatus } from '../../../../services/graphql/types/graphql';
import { ossImage } from '../../../../utils/ossImage';

const getGraphQLErrorMessage = (error: unknown, fallback: string): string => {
  if (error && typeof error === 'object' && 'graphQLErrors' in error) {
    const message = (error as { graphQLErrors?: Array<{ message?: string }> }).graphQLErrors?.[0]
      ?.message;
    if (message) {
      return message;
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
};

const uploadReviewMedia = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.post<{ url?: string }>('/upload/review-media', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  if (!response.data.url) {
    throw new Error('上传评语图片失败');
  }

  return response.data.url;
};

export const ReviewEditorTab = ({
  submissionId,
  submissionStatus,
}: {
  submissionId: string;
  submissionStatus: SubmissionStatus;
}): JSX.Element => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [comment, setComment] = useState('');
  const [pendingMediaUrls, setPendingMediaUrls] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);

  const { data, refetch } = useQuery<{ submissionReviews: SubmissionReviewItem[] }>(
    SUBMISSION_REVIEWS,
    { variables: { submissionId } },
  );

  const [addReview, { loading: adding }] = useMutation(ADD_SUBMISSION_REVIEW);
  const [publishReviews, { loading: publishing }] = useMutation(PUBLISH_SUBMISSION_REVIEWS, {
    refetchQueries: ['GetTeacherEvaluationPage', 'GetGradeBookPageData'],
  });

  const reviews = data?.submissionReviews ?? [];
  const unpublishedReviews = reviews.filter((review) => !review.publishedAt);
  const publishedReviews = reviews.filter((review) => review.publishedAt);
  const hasDraft = comment.trim().length > 0 || pendingMediaUrls.length > 0;
  const canPublish =
    submissionStatus === SubmissionStatus.Submitted ||
    submissionStatus === SubmissionStatus.TaApproved ||
    submissionStatus === SubmissionStatus.Unsubmitted;
  const isReadOnlyView =
    submissionStatus === SubmissionStatus.ChangeRequested ||
    submissionStatus === SubmissionStatus.Resubmitted;
  const canSend = canPublish && (unpublishedReviews.length > 0 || hasDraft);

  const onPickImage = () => {
    fileInputRef.current?.click();
  };

  const onImageSelected: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) {
      return;
    }

    setUploading(true);
    try {
      const url = await uploadReviewMedia(file);
      setPendingMediaUrls((prev) => [...prev, url]);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '上传失败');
    } finally {
      setUploading(false);
    }
  };

  const appendDraftReview = async () => {
    if (!hasDraft) {
      return;
    }

    await addReview({
      variables: {
        input: {
          submissionId,
          comment: comment.trim(),
          mediaUrls: pendingMediaUrls,
        },
      },
    });
    await refetch();
    setComment('');
    setPendingMediaUrls([]);
  };

  const onAddReview = async () => {
    if (!hasDraft) {
      toast.info('请填写文字或上传图片');
      return;
    }

    try {
      await toast.promise(appendDraftReview(), {
        pending: '正在追加评语',
        success: '评语已追加',
        error: {
          render({ data }) {
            return getGraphQLErrorMessage(data, '追加评语失败');
          },
        },
      });
    } catch {
      // toast.promise already surfaced the error
    }
  };

  const onPublish = async () => {
    if (!canSend) {
      return;
    }

    try {
      await toast.promise(
        (async () => {
          if (hasDraft) {
            await appendDraftReview();
          }
          await publishReviews({ variables: { submissionId } });
          await refetch();
        })(),
        {
          pending: '正在发送给学生',
          success: '评语已发送，等待学生修改',
          error: {
            render({ data }) {
              return getGraphQLErrorMessage(data, '发送失败');
            },
          },
        },
      );
    } catch {
      // toast.promise already surfaced the error
    }
  };

  return (
    <div className="flex flex-col px-4 pb-4 text-white">
      <header className="text-[14px] leading-[20px] tracking-[0.1em] opacity-60 mb-3">
        {isReadOnlyView ? '已发送的评语' : '作业评语（发送前可多次追加）'}
      </header>

      {!isReadOnlyView && (
        <>
          <textarea
            className="bg-transparent text-white text-[14px] leading-[20px] w-full min-h-[80px] border border-white border-dashed p-3 mb-3"
            placeholder="输入文字评语..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            disabled={!canPublish}
          />

          {pendingMediaUrls.length > 0 && (
            <div className="grid grid-cols-3 gap-2 mb-3">
              {pendingMediaUrls.map((url) => (
                <img
                  key={url}
                  src={ossImage(url, { resize: { w: 240, m: 'mfit' } })}
                  alt=""
                  className="w-full aspect-square object-cover rounded"
                />
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-2 mb-4">
            <button
              type="button"
              className="px-3 py-2 bg-white text-black rounded disabled:opacity-50"
              onClick={onPickImage}
              disabled={!canPublish || uploading}
            >
              {uploading ? '上传中...' : '添加图片'}
            </button>
            <button
              type="button"
              className="px-3 py-2 bg-[#e8ed05] text-black rounded disabled:opacity-50"
              onClick={onAddReview}
              disabled={!canPublish || adding || !hasDraft}
            >
              追加评语
            </button>
            <button
              type="button"
              className="px-3 py-2 bg-[#E8A317] text-black rounded disabled:opacity-50"
              onClick={onPublish}
              disabled={!canSend || publishing}
            >
              发送给学生
            </button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onImageSelected}
          />
        </>
      )}

      <div className="flex flex-col gap-3 max-h-[240px] overflow-auto">
        {(isReadOnlyView ? publishedReviews : reviews).map((review) => (
          <div
            key={review.reviewId}
            className={cx('border border-dashed p-3 rounded', {
              'border-[#E8A317]': !review.publishedAt,
              'border-white/40': review.publishedAt,
            })}
          >
            <div className="text-xs opacity-60 mb-1">
              {review.authorRole === 'teacher' ? '任课教师' : '班主任'}
              {review.publishedAt ? ' · 已发送' : ' · 待发送'}
            </div>
            {review.comment && <div className="text-sm whitespace-pre-wrap">{review.comment}</div>}
            {review.mediaUrls?.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-2">
                {review.mediaUrls.map((url) => (
                  <img
                    key={url}
                    src={ossImage(url, { resize: { w: 240, m: 'mfit' } })}
                    alt=""
                    className="w-full aspect-square object-cover rounded"
                  />
                ))}
              </div>
            )}
          </div>
        ))}
        {reviews.length === 0 && !hasDraft && !isReadOnlyView && (
          <div className="text-sm opacity-50">填写评语后可直接发送，或先追加再发送。</div>
        )}
        {isReadOnlyView && publishedReviews.length === 0 && (
          <div className="text-sm opacity-50">暂无已发送的评语。</div>
        )}
      </div>
    </div>
  );
};
